import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box, Column, Divider, Row, SmartTooltip } from '~/commonComponents';
import { JobPostingStatus, JobPostingStatusMapper } from '~/domain/PermanentJobs';
import { useJobProfessionDisplay } from '~/hooks';
import { WarningSolidIcon } from '~/images/icons';
import UserIcon from '~/images/user-icon.svg';
import { Badge } from '../../../components';
import { TJobCardProps } from './Types';

export const JobCard = ({
  candidateCount,
  closedAt,
  id,
  office,
  onsiteInterviewsCount,
  professionType,
  publishedAt,
  status,
  subProfession,
  title,
}: TJobCardProps) => {
  const navigate = useNavigate();

  const fmtTimestamp = useMemo(() => {
    if (status === JobPostingStatus.Closed) {
      return `Closed on ${closedAt}`;
    }

    return `Posted on ${publishedAt}`;
  }, [closedAt, publishedAt, status]);

  const hasNoInterviewSlots = onsiteInterviewsCount === 0;

  const isActiveJob = status === JobPostingStatus.Active;
  const isSuspendedJob = status === JobPostingStatus.Suspended;

  const isShowInterviewCounter =
    isActiveJob && onsiteInterviewsCount !== undefined && onsiteInterviewsCount <= 3;
  const totalCandidates = candidateCount ?? 0;

  const professionTypeDisplay = useJobProfessionDisplay({
    profession: professionType,
    subProfession,
  });

  return (
    <Row
      alignItems="center"
      backgroundColor={colors.shades[0]}
      borderRadius={10}
      className="cursor-pointer shadow-xs hover__shadow-sm"
      data-cy="active-job-card"
      display="grid"
      gridTemplateColumns="1fr 20% 20%"
      key={id}
      paddingBlock={12}
      onClick={() => navigate(`/permanent-jobs/job-overview/${id}`)}
    >
      <Column gap={6} marginLeft={36}>
        <Row alignItems="center" gap={8}>
          <Typography as="span" bold style={{ textDecoration: 'underline' }}>
            {title || 'UNTITLED'}
          </Typography>
          {isSuspendedJob && (
            <SmartTooltip
              content="Awaiting Payment"
              hideArrow
              position="bottom"
              style={{
                Content: { fontSize: 16, paddingBlock: 1, paddingInline: 10 },
                Portal: { borderRadius: 3 },
              }}
            >
              <WarningSolidIcon color={colors.warning[500]} size={18} />
            </SmartTooltip>
          )}
        </Row>
        <Typography as="span">{office?.name}</Typography>
        <Row alignItems="center" gap={12}>
          <Typography as="span">{professionTypeDisplay || 'No Professional Type Selected'}</Typography>
          <Divider width={1} height={12} marginVertical={0} />
          <Typography italic color={colors.neutral[400]} style={{ fontSize: 14 }}>
            {fmtTimestamp}
          </Typography>
          {isShowInterviewCounter && (
            <Badge
              label={`${onsiteInterviewsCount} INTERVIEWS LEFT`}
              variant={hasNoInterviewSlots ? 'error' : 'warning'}
            />
          )}
        </Row>
      </Column>
      <Row alignItems="center" justifyContent="center" gap={4}>
        <img alt="user-icon" src={UserIcon} height={24} width={24} />
        <Typography as="span" bold color={colors.secondary[500]} size="heading_4">
          {totalCandidates}
        </Typography>
      </Row>
      <Row alignItems="center" gap={8}>
        <Box
          backgroundColor={JobPostingStatusMapper.getColor(status)}
          borderRadius="100%"
          height={10}
          width={10}
        />
        <Typography as="span">{JobPostingStatusMapper.toHumanReadable(status)}</Typography>
      </Row>
    </Row>
  );
};
